import { useMemo, useState } from "react";
import { t } from "i18next";
import type { PaginationState } from "@tanstack/react-table";

import { pageCount } from "@/core/api/pagination";
import {
  DataTableClar,
  type ColumnsClarFactory,
} from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { CopyButton } from "@/core/components/ui/copy-button";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { useSavingsVaultLockEvents } from "@/features/savings/api/use-savings-vault-lock-events";
import type { SavingsLockEventDto } from "@/features/savings/api/dto";
import type { LockEventActor, LockEventType } from "@/features/savings/api/enums";

/** Ligne de l'historique : `DataTableClar` exige un `id` requis. */
export type SavingsLockEventRow = SavingsLockEventDto & { id: string };

const ACTION_VARIANTS: Record<
  LockEventType,
  "secondary" | "warning" | "success"
> = {
  LOCKED: "secondary",
  EXTENDED: "warning",
  EARLY_UNLOCKED: "success",
};

/**
 * Colonnes de l'historique des blocages (`GET .../lock-events`) — lecture
 * paginée GET, aucun filtre documenté côté API : tout en `denied`.
 * `performedByKeycloakUserId` / `approvalRequestId` ne sont renseignés que
 * pour le back-office (§6.10).
 */
const lockEventsColumns: ColumnsClarFactory<SavingsLockEventRow> = () => [
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: t("savings.vaults.lockEvents.columns.createdAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "action",
    accessorKey: "action",
    header: t("savings.vaults.lockEvents.columns.action"),
    cell: ({ getValue }) => {
      const action = getValue() as LockEventType | undefined;
      return action ? (
        <Badge variant={ACTION_VARIANTS[action] ?? "outline"}>
          {t(`savings.enums.lockEventType.${action}`)}
        </Badge>
      ) : (
        "—"
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "previousLockedUntil",
    accessorKey: "previousLockedUntil",
    header: t("savings.vaults.lockEvents.columns.previousLockedUntil"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "newLockedUntil",
    accessorKey: "newLockedUntil",
    header: t("savings.vaults.lockEvents.columns.newLockedUntil"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "performedBy",
    accessorKey: "performedBy",
    header: t("savings.vaults.lockEvents.columns.performedBy"),
    cell: ({ row }) => {
      const actor = row.original.performedBy as LockEventActor | undefined;
      const actorId = row.original.performedByKeycloakUserId;
      if (!actor) return "—";
      return (
        <span className="inline-flex items-center gap-1">
          <Badge variant="outline">
            {t(`savings.enums.lockEventActor.${actor}`)}
          </Badge>
          {actorId ? (
            <NoTranslate as="span" className="font-mono text-xs">
              {actorId.slice(0, 8)}…
            </NoTranslate>
          ) : null}
        </span>
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "approvalRequestId",
    accessorKey: "approvalRequestId",
    header: t("savings.vaults.lockEvents.columns.approvalRequestId"),
    cell: ({ getValue }) => {
      const approvalId = (getValue() as string | null | undefined)?.trim();
      if (!approvalId) return "—";
      return (
        <span className="inline-flex items-center gap-1">
          <NoTranslate as="span" className="font-mono text-xs">
            {approvalId.slice(0, 8)}…
          </NoTranslate>
          <CopyButton value={approvalId} />
        </span>
      );
    },
    meta: { type: "denied" },
  },
];

/**
 * Onglet « Blocages » du détail coffre : LOCKED / EXTENDED /
 * EARLY_UNLOCKED (du plus récent au plus ancien) avec échéances avant /
 * après et acteur.
 */
export function VaultLockEventsSection({ vaultId }: { vaultId: string }) {
  const { t } = useTypedTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useSavingsVaultLockEvents(vaultId, params);

  const rows = useMemo(
    () =>
      (data?.items ?? []).filter((e): e is SavingsLockEventRow => Boolean(e.id)),
    [data]
  );

  return (
    <DataTableClar
      data={rows}
      columnsClar={lockEventsColumns}
      title={t("savings.vaults.lockEvents.title")}
      exportFileName="savings-vault-lock-events"
      paginationMode="server"
      serverPageCount={data ? pageCount(data) : 0}
      totalItemsCount={data?.total ?? 0}
      pagination={pagination}
      onPaginationChange={setPagination}
      loading={{ isLoading, isFetching }}
      error={
        error
          ? { message: error.message, refetch: () => void refetch() }
          : undefined
      }
    />
  );
}
